import React, { useEffect, useRef, useState } from 'react';
import { BookOpen, ChevronRight, FileText, Globe, Heart, Lock, MessageCircle, Share2 } from 'lucide-react';
import { longDate } from '../../utils/articles';
import { initials } from '../../utils/share';
import { SharePopover as SharePopoverView } from './SharePopover';
import './Reader.css';

/**
 * Cabeçalho na largura toda: tags e visibilidade, título, linha fina e a linha do autor
 * (entre bordas) com as ações à direita.
 */
export function ReaderHeader({ article, actions, showVisibility = false }) {
  return (
    <header className="rd-head">
      <span className="rd-head__eyebrow">
        {article.tags.length > 0 && <span>{article.tags.map(t => `#${t.name}`).join(' · ')}</span>}
        {showVisibility && <VisibilityBadge visibility={article.visibility} />}
      </span>
      <h1 className="rd-head__title">{article.title}</h1>
      {article.subtitle && <p className="rd-head__lead">{article.subtitle}</p>}
      <div className="rd-head__row">
        <span className="rd-head__by">
          <span className="rd-avatar">{article.author.initials || initials(article.author.name)}</span>
          <span className="rd-head__who">
            <strong>{article.author.name}</strong>
            <span>
              {article.publishedAt ? longDate(article.publishedAt) : 'Rascunho'} · {article.readingMinutes} min de leitura
            </span>
          </span>
        </span>
        {actions && <div className="rd-head__actions" data-tour="artigo-acoes">{actions}</div>}
      </div>
    </header>
  );
}

/** Botão de ação da leitura (34px, contorno). */
export function ActionButton({ icon: Icon, size = 'md', className = '', children, ...props }) {
  return (
    <button type="button" className={`rd-act ${size === 'sm' ? 'rd-act--sm' : ''} ${className}`} {...props}>
      {Icon && <Icon size={size === 'sm' ? 13 : 15} strokeWidth={2.2} aria-hidden="true" />}
      {children}
    </button>
  );
}

export function VisibilityBadge({ visibility }) {
  return visibility === 'public'
    ? <span className="rd-vis rd-vis--public"><Globe size={11} strokeWidth={2.6} aria-hidden="true" />Público</span>
    : <span className="rd-vis rd-vis--members"><Lock size={11} strokeWidth={2.6} aria-hidden="true" />Só alunos</span>;
}

/**
 * Progresso de leitura pelo scroll do corpo: % lido, seção atual e seções já passadas.
 * @param onProgress chamado quando o % sobe (para salvar no servidor)
 */
export function useReadingProgress(bodyRef, headings, onProgress) {
  const [state, setState] = useState({ pct: 0, active: headings[0]?.id || null });
  const max = useRef(0);
  const key = headings.map(h => h.id).join('|');

  useEffect(() => {
    const onScroll = () => {
      const el = bodyRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const seen = window.innerHeight - rect.top;
      const pct = Math.max(0, Math.min(100, Math.round((seen / rect.height) * 100)));
      // Seção atual: a última cujo título passou do terço de cima da tela; no fim, a última
      const line = window.innerHeight * 0.35;
      let active = headings[0]?.id || null;
      for (const h of headings) {
        const node = document.getElementById(h.id);
        if (node && node.getBoundingClientRect().top < line) active = h.id;
      }
      if (pct >= 100 && headings.length) active = headings[headings.length - 1].id;
      setState({ pct, active });
      if (pct > max.current) {
        max.current = pct;
        onProgress?.(pct);
      }
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bodyRef, key]);

  return state;
}

/** "Neste artigo": % lido, barra de progresso, índice com a seção atual e um rodapé opcional. */
export function TocCard({ headings, pct, active, done, footer }) {
  const go = (id) => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  const finished = done || pct >= 100;
  return (
    <section className="rd-card rd-toccard">
      <div className="rd-toccard__main">
        <div className="rd-toccard__head">
          <h2 className="rd-card__title">Neste artigo</h2>
          <span className="rd-progress__label">{finished ? 'Leitura concluída' : `${pct}% lido`}</span>
        </div>
        <div className="rd-progress"><span style={{ width: `${done ? 100 : pct}%` }} /></div>
        {headings.length > 0 && (
          <nav className="rd-toc" aria-label="Seções do artigo">
            {headings.map(h => (
              <button
                key={h.id}
                type="button"
                className={`rd-toc__item ${!done && h.id === active ? 'is-active' : ''}`}
                aria-current={!done && h.id === active ? 'location' : undefined}
                onClick={() => go(h.id)}
              >
                {h.text}
              </button>
            ))}
          </nav>
        )}
      </div>
      {footer && <div className="rd-toccard__foot">{footer}</div>}
    </section>
  );
}

/** Rodapé do sumário: curtidas · comentários · compartilhamentos. */
export function EngagementLine({ likes, comments, shares, liked = false }) {
  return (
    <div className="rd-engage">
      <span className={liked ? 'is-liked' : ''} title="Curtidas">
        <Heart size={13} aria-hidden="true" />{likes} {likes === 1 ? 'curtida' : 'curtidas'}
      </span>
      <span title="Comentários"><MessageCircle size={13} aria-hidden="true" />{comments}</span>
      <span title="Compartilhamentos"><Share2 size={13} aria-hidden="true" />{shares}</span>
    </div>
  );
}

/** "Sobre o autor" como rodapé do card do sumário. */
export function AuthorFooter({ author }) {
  const courses = author.courses?.length ? ` · ${author.courses.join(', ')}` : '';
  return (
    <div className="rd-author">
      <span className="rd-avatar">{author.initials || initials(author.name)}</span>
      <span className="rd-author__text">
        <span className="rd-author__label">Sobre o autor</span>
        <strong>{author.name}</strong>
        {author.articles != null && (
          <span>{author.articles} {author.articles === 1 ? 'artigo' : 'artigos'}{courses}</span>
        )}
      </span>
    </div>
  );
}

/** "Conecta com": tópico e aula vinculados. */
export function LinksCard({ links, onOpen }) {
  if (!links?.length) return null;
  return (
    <section className="rd-card">
      <h2 className="rd-card__title rd-card__title--tight">Conecta com</h2>
      <div className="rd-links">
        {links.map(l => (
          <button key={`${l.kind}-${l.lessonId || l.topicId}`} type="button" className="rd-link" onClick={() => onOpen(l)}>
            <span className={`rd-link__icon ${l.kind === 'topic' ? 'rd-link__icon--topic' : ''}`}>
              {l.kind === 'topic' ? <BookOpen size={17} aria-hidden="true" /> : <FileText size={17} aria-hidden="true" />}
            </span>
            <span className="rd-link__text">
              <span>{l.kind === 'topic' ? `Tópico · ${l.courseName}` : `Aula ${l.lessonNumber ?? ''}`}</span>
              <strong>{l.name}</strong>
            </span>
            <ChevronRight size={16} aria-hidden="true" />
          </button>
        ))}
      </div>
    </section>
  );
}

/** Botão "Compartilhar" (popover no desktop, bottom sheet no celular). Ver SharePopover.js. */
export function SharePopover(props) {
  return <SharePopoverView {...props} ActionButton={ActionButton} />;
}
